import React, { useState, useEffect } from 'react';
import './ServerStatus.css';
import { OrganSegAPIEndpoint, APIOrganSegStopInstance } from '../../helper/APIFunctions';

function ServerStatus({ onSegmentClick, showSegmentButton = false }) {
  const [status, setStatus] = useState('checking');
  const [serverInfo, setServerInfo] = useState(null);
  const [lastCheck, setLastCheck] = useState(null);
  const [failureStartTime, setFailureStartTime] = useState(Date.now());
  const [actionInProgress, setActionInProgress] = useState(false);
  const [userStartedInstance, setUserStartedInstance] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  
  // Draggable state - bottom-left corner by default
  const [position, setPosition] = useState(() => ({
    x: 20,
    y: typeof window !== 'undefined' ? window.innerHeight - 120 : 500
  }));
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isMinimized, setIsMinimized] = useState(true); // Start minimized by default
  
  // Listen for server starting events from other components
  useEffect(() => {
    const handleServerStarting = () => {
      setUserStartedInstance(true);
      setStatus('initializing');
      setFailureStartTime(Date.now());
    };
    
    window.addEventListener('serverStarting', handleServerStarting);
    return () => window.removeEventListener('serverStarting', handleServerStarting);
  }, []);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await fetch(`${OrganSegAPIEndpoint}/api/health`, {
          method: 'GET',
          signal: AbortSignal.timeout(5000)
        });

        if (response.ok) {
          const data = await response.json();
          
          // Check actual backend status, not just HTTP 200
          if (data.status === 'healthy' || data.status === 'ok') {
            // Don't change status if we're in the process of stopping
            if (isStopping) {
              setStatus('stopping');
              setServerInfo(null);
              setLastCheck(new Date());
            } else {
              setStatus('online');
              setServerInfo({
                gpu: data.gpu_available ? `${data.gpu_name}` : 'CPU Only',
                idleMinutes: data.idle_minutes?.toFixed(1) || 0,
                idleSeconds: data.idle_seconds || 0
              });
              setFailureStartTime(null);
              setLastCheck(new Date());
              setUserStartedInstance(false); // Clear flag once online
            }
          } else if (data.status === 'starting') {
            // Proxy says "starting" which means no instance is available
            // Only show "initializing" if user actually clicked start
            if (userStartedInstance) {
              setStatus('initializing');
            } else if (isStopping) {
              setStatus('stopping');
            } else {
              setStatus('offline');
            }
            setServerInfo(null);
            setLastCheck(new Date());
          } else {
            // Unknown status
            handleFailure();
          }
        } else {
          handleFailure();
        }
      } catch (error) {
        handleFailure();
      }
    };

    const handleFailure = () => {
      const now = Date.now();
      
      // If we're stopping, show stopping status until confirmed offline
      if (isStopping) {
        // Check if it's been stopping for more than 2 minutes, then assume it's offline
        const timeStopping = failureStartTime ? (now - failureStartTime) / 1000 : 0;
        if (timeStopping > 120) {
          setIsStopping(false);
          setStatus('offline');
        } else {
          setStatus('stopping');
        }
        setServerInfo(null);
        setLastCheck(new Date());
        return;
      }
      
      // Only show initializing if user explicitly started the instance
      // Keep showing initializing for up to 5 minutes after user clicks start
      if (userStartedInstance) {
        // Check how long it's been since user started (from when failureStartTime was reset)
        const timeSinceStart = failureStartTime ? (now - failureStartTime) / 1000 : 0;
        
        if (timeSinceStart < 300) {
          // Give 5 minutes for instance to fully boot and server to start
          setStatus('initializing');
          setServerInfo(null);
          setLastCheck(new Date());
          return;
        } else {
          // After 5 minutes, give up and show offline
          setUserStartedInstance(false);
          setStatus('offline');
        }
      } else {
        // User didn't start, so check if we should show offline
        // Give a grace period (30 seconds) before marking as offline
        // This prevents false "offline" status during heavy processing
        if (!failureStartTime) {
          setFailureStartTime(now);
        }
        
        const timeSinceFailure = (now - failureStartTime) / 1000;
        if (timeSinceFailure > 30) {
          // After 30 seconds of failures, mark as offline
          setStatus('offline');
          // Clear stopping flag when we've confirmed offline
          if (isStopping) {
            setIsStopping(false);
          }
        } else {
          // Keep showing last known status during grace period
          // Don't change status yet - might just be busy processing
        }
      }
      
      setServerInfo(null);
      setLastCheck(new Date());
    };

    checkStatus();
    // Poll more frequently when initializing or stopping
    const pollInterval = (status === 'initializing' || status === 'stopping') ? 5000 : 10000;
    const interval = setInterval(checkStatus, pollInterval);

    return () => clearInterval(interval);
  }, [failureStartTime, userStartedInstance, status, isStopping]);

  const getStatusText = () => {
    switch (status) {
      case 'online': return 'Online';
      case 'offline': return 'Offline';
      case 'initializing': return 'Initializing...';
      case 'stopping': return 'Stopping...';
      default: return 'Unknown';
    }
  };

  const getIdleWarning = () => {
    if (!serverInfo || status !== 'online') return null;
    const { idleMinutes } = serverInfo;
    if (idleMinutes >= 13) {
      return '⚠️ Auto-shutdown in ~2 min';
    } else if (idleMinutes >= 10) {
      return '⏰ Auto-shutdown in ~5 min';
    }
    return null;
  };

  const handleStopInstance = () => {
    if (!window.confirm('Are you sure you want to stop the GPU instance? Any running segmentation will be interrupted.')) {
      return;
    }
    setActionInProgress(true);
    setIsStopping(true);
    setStatus('stopping');
    setFailureStartTime(Date.now()); // Start timeout tracking for stopping
    APIOrganSegStopInstance(
      () => {
        setServerInfo(null);
        setActionInProgress(false);
        setUserStartedInstance(false);
        // Keep isStopping=true, will be cleared when health check confirms offline
      },
      (error) => {
        alert(`Failed to stop instance: ${error}`);
        setActionInProgress(false);
        setIsStopping(false);
        setStatus('online'); // Revert to online if stop failed
        setFailureStartTime(null); // Clear timeout tracking
      }
    );
  };

  const isAwake = status === 'online';

  // Drag handlers
  const handleMouseDown = (e) => {
    if (e.target.closest('.server-btn') || e.target.closest('.segment-data-btn')) {
      return; // Don't start drag when clicking buttons
    }
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      
      // Constrain to viewport
      const maxX = window.innerWidth - 300;
      const maxY = window.innerHeight - 100;
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  return (
    <div 
      className={`server-status ${isDragging ? 'dragging' : ''} ${isMinimized ? 'minimized' : ''}`}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        cursor: isDragging ? 'grabbing' : 'grab',
        zIndex: 1000
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Always show moon/sun animation */}
      <div className="minimized-preview">
        <div className={`planet-wrapper ${isAwake ? 'day' : ''}`}>
          <div className="star pos-star1"></div>
          <div className="star pos-star2"></div>
          <div className="star pos-star3"></div>
          <div className="zzz1"></div>
          <div className="zzz2"></div>
          <div className="zzz3"></div>
          <div className="planet"> 
            <div className="face">
              <div className="eye">
                <div className="eye-in"></div>
              </div>
              <div className="mouth"></div>
              <div className="eye">
                <div className="eye-in"></div>
              </div>
            </div>
          </div>
        </div>
        <button 
          className="expand-btn"
          onClick={(e) => {
            e.stopPropagation();
            setIsMinimized(!isMinimized);
          }}
          title={isMinimized ? "Show server details" : "Minimize"}
        >
          {isMinimized ? '⤢' : '─'}
        </button>
      </div>
      
      {!isMinimized && (
        <div className="server-status-content">
          {/* Minimize button when expanded */}
          <button 
            className="minimize-btn-expanded"
            onClick={(e) => {
              e.stopPropagation();
              setIsMinimized(true);
            }}
            title="Minimize"
          >
            ─
          </button>
          
      <div className="status-indicator">
        {/* Moon/Sun Animation */}
        <div className={`planet-wrapper ${isAwake ? 'day' : ''}`}>
          <div className="star pos-star1"></div>
          <div className="star pos-star2"></div>
          <div className="star pos-star3"></div>
          <div className="zzz1"></div>
          <div className="zzz2"></div>
          <div className="zzz3"></div>
          <div className="planet"> 
            <div className="face">
              <div className="eye">
                <div className="eye-in"></div>
              </div>
              <div className="mouth"></div>
              <div className="eye">
                <div className="eye-in"></div>
              </div>
            </div>
          </div>
        </div>
        <span className="status-text">
          <strong>Server:</strong> {getStatusText()}
        </span>
        <div className="server-controls">
          {status === 'online' && (
            <div className="stop-btn-container">
              <button 
                onClick={handleStopInstance}
                disabled={actionInProgress}
                className="server-btn stop-btn"
                title="Stop GPU instance (saves costs)"
              >
                Stop Server
              </button>
              <span className="stop-btn-subtitle">Stops usage charge</span>
            </div>
          )}
        </div>
      </div>
      {status === 'online' && serverInfo && (
        <div className="status-details">
          <span className="status-detail">
            {serverInfo.gpu}
          </span>
          <span className="status-detail">
            ⏱️ Idle: {serverInfo.idleMinutes} min
          </span>
          {getIdleWarning() && (
            <span className="status-warning">
              {getIdleWarning()}
            </span>
          )}
        </div>
      )}
      {status === 'initializing' && (
        <div className="status-details">
          <span className="status-detail offline-message">
            Server is starting up... This takes ~2-3 minutes
          </span>
        </div>
      )}
      {status === 'stopping' && (
        <div className="status-details">
          <span className="status-detail offline-message">
            Server is shutting down...
          </span>
        </div>
      )}
      {status === 'offline' && (
        <div className="status-details">
          <span className="status-detail offline-message">
            Server is offline. Upload a file to automatically start the GPU instance.
          </span>
        </div>
      )}
      
      {lastCheck && (
        <div className="last-check">
          Updated {new Date().toLocaleTimeString()}
        </div>
      )}
        </div>
      )}
    </div>
  );
}

export default ServerStatus;

